import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { MenuItem, Stack } from '@mui/material';

import { InfoField, InfoFieldChangeHandler } from '../../../../../components';

import { UTCToDateTime } from '../../../../../utils/dates';
import { useGetCountriesQuery } from '../../../../../api';
import type { Address } from '../../../../../types';

type AddressInfoProps = {
  address: Address | undefined;
  isEditMode: boolean;
  fields: Partial<Address> | null;
  fieldsHandler: InfoFieldChangeHandler;
  isLoading?: boolean;
};

export const AddressInfo: FC<AddressInfoProps> = (props) => {
  const { isEditMode, address, fields, fieldsHandler, isLoading } = props;
  const { t } = useTranslation();

  // api
  const { data: countries } = useGetCountriesQuery();

  // elements
  const countrySelectOptions = countries?.countries.map(({ alpha2code, name }) => {
    return (
      <MenuItem key={alpha2code} value={alpha2code}>
        {name}
      </MenuItem>
    );
  });

  return (
    <Stack direction="column" spacing={1}>
      <InfoField
        name={t('profile-details.address.country')}
        value={address?.country_code}
        formattedValue={
          address?.country_code
            ? countries?.countries.find((c) => c.alpha2code === address?.country_code)!.name
            : undefined
        }
        editProps={{ withEditMode: true, isEditMode }}
        inputProps={{
          name: 'country_code',
          value: fields?.country_code ?? '',
          onChange: fieldsHandler,
          componentType: 'select',
          selectOptions: countrySelectOptions,
        }}
        isLoading={isLoading}
      />

      <InfoField
        name={t('profile-details.address.state')}
        value={address?.state}
        editProps={{ withEditMode: true, isEditMode }}
        inputProps={{
          name: 'state',
          value: fields?.state ?? '',
          onChange: fieldsHandler,
        }}
        isLoading={isLoading}
        withEmptyValue
      />

      <InfoField
        name={t('profile-details.address.city')}
        value={address?.city}
        editProps={{ withEditMode: true, isEditMode }}
        inputProps={{
          name: 'city',
          value: fields?.city ?? '',
          onChange: fieldsHandler,
        }}
        isLoading={isLoading}
        withEmptyValue
      />

      <InfoField
        name={t('profile-details.address.zip-code')}
        value={address?.zip_code}
        editProps={{ withEditMode: true, isEditMode }}
        inputProps={{
          name: 'zip_code',
          value: fields?.zip_code ?? '',
          onChange: fieldsHandler,
        }}
        isLoading={isLoading}
        withEmptyValue
      />

      <InfoField
        name={t('profile-details.address.street')}
        value={address?.street}
        editProps={{ withEditMode: true, isEditMode }}
        inputProps={{
          name: 'street',
          value: fields?.street ?? '',
          onChange: fieldsHandler,
        }}
        isLoading={isLoading}
        withEmptyValue
      />

      <InfoField
        name={t('profile-details.address.building-number')}
        value={address?.building_number}
        editProps={{ withEditMode: true, isEditMode }}
        inputProps={{
          name: 'building_number',
          value: fields?.building_number ?? '',
          onChange: fieldsHandler,
        }}
        isLoading={isLoading}
        withEmptyValue
      />

      <InfoField
        name={t('profile-details.address.flat-number')}
        value={address?.flat_number}
        editProps={{ withEditMode: true, isEditMode }}
        inputProps={{
          name: 'flat_number',
          value: fields?.flat_number ?? '',
          onChange: fieldsHandler,
        }}
        isLoading={isLoading}
        withEmptyValue
      />

      <InfoField
        name={t('profile-details.address.last-modified')}
        value={address?.row_start}
        formattedValue={
          address?.row_start ? UTCToDateTime(address?.row_start as string) : undefined
        }
        withoutDivider
      />
    </Stack>
  );
};
